import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, User, Store, ChevronRight } from "lucide-react";

const BASE_DOMAIN = "pickfood.it";
const slugify = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function Choice({ onPartner }) {
  const nav = useNavigate();
  return (
    <div className="w-full max-w-md">
      <div className="mb-6"><BrandLogo name="Pickfood" size={30} /></div>
      <h1 className="font-head text-2xl font-bold">Crea il tuo account</h1>
      <p className="text-white/55 text-sm mb-6">Scegli come vuoi usare Pickfood.</p>
      <div className="space-y-3">
        <button onClick={() => nav("/account/login?tab=register")} data-testid="register-choice-customer"
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFC400] hover:bg-white/[0.07] transition-colors text-left">
          <span className="w-11 h-11 shrink-0 rounded-xl bg-[#FFC400]/15 grid place-items-center text-[#FFC400]"><User size={20} /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-white">Sono un cliente</span>
            <span className="block text-sm text-white/55">Ordina, ritira e accumula coupon dai tuoi locali preferiti</span>
          </span>
          <ChevronRight size={18} className="text-white/40 shrink-0" />
        </button>
        <button onClick={onPartner} data-testid="register-choice-partner"
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFC400] hover:bg-white/[0.07] transition-colors text-left">
          <span className="w-11 h-11 shrink-0 rounded-xl bg-[#FFC400]/15 grid place-items-center text-[#FFC400]"><Store size={20} /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-white">Ho un locale (P. IVA)</span>
            <span className="block text-sm text-white/55">Attiva la tua web app: menu, ordini e incassi in pochi minuti</span>
          </span>
          <ChevronRight size={18} className="text-white/40 shrink-0" />
        </button>
      </div>
      <p className="text-sm text-white/50 mt-6 text-center">Hai già un account? <Link to="/account/login" className="text-[#FFC400] hover:underline" data-testid="register-goto-login">Accedi</Link></p>
    </div>
  );
}

function PartnerForm({ onBack }) {
  const { registerPartner } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ commercial_name: "", business_name: "", vat_number: "", name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await registerPartner({
        commercial_name: f.commercial_name.trim(),
        business_name: f.business_name.trim(),
        vat_number: f.vat_number.trim(),
        name: f.name.trim(),
        email: f.email.trim(),
        password: f.password,
        phone: f.phone.trim() || null,
      });
      toast.success("Account partner creato! Controlla l'email per la verifica.");
      nav("/manage");
      return data;
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label htmlFor={k} className="text-white/80">{label}</Label>
      <Input id={k} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`partner-${k}`}
        className="h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/35" {...props} />
    </div>
  );

  return (
    <div className="w-full max-w-md">
      <button onClick={onBack} data-testid="partner-back" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-4"><ArrowLeft size={15} /> Indietro</button>
      <div className="mb-5"><BrandLogo name="Pickfood" size={30} /></div>
      <h1 className="font-head text-2xl font-bold">Registra il tuo locale</h1>
      <p className="text-white/55 text-sm mb-6">Attivazione immediata. Nessun addebito: la sottoscrizione parte in prova.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="commercial_name" className="text-white/80">Nome del locale *</Label>
          <Input id="commercial_name" value={f.commercial_name} onChange={(e) => set("commercial_name", e.target.value)} data-testid="partner-commercial_name" required
            className="h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/35" placeholder="Es. Paninoteca da Mario" />
          {f.commercial_name.trim() && (
            <p className="text-xs text-white/50 mt-1.5" data-testid="partner-slug-preview">
              Il tuo indirizzo: <span className="font-mono text-[#FFC400]">{slugify(f.commercial_name) || "locale"}.{BASE_DOMAIN}</span>
            </p>
          )}
        </div>
        {field("business_name", "Ragione sociale *", { required: true, placeholder: "Es. Mario Rossi S.r.l." })}
        {field("vat_number", "Partita IVA *", { required: true, placeholder: "IT01234567890" })}
        {field("name", "Nome referente *", { required: true, placeholder: "Il tuo nome e cognome" })}
        {field("email", "Email *", { required: true, type: "email", placeholder: "nome@esempio.it" })}
        {field("phone", "Telefono", { placeholder: "+39 ..." })}
        {field("password", "Password *", { required: true, type: "password", placeholder: "Almeno 6 caratteri" })}
        <Button type="submit" disabled={busy} data-testid="partner-submit"
          className="w-full h-11 bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] rounded-full text-base font-semibold">
          {busy ? <Loader2 className="animate-spin" /> : "Crea account partner"}
        </Button>
      </form>
    </div>
  );
}

export default function Register() {
  const [step, setStep] = useState("choice");
  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-4 h-16 flex items-center">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="register-home"><ArrowLeft size={16} /> Torna al portale</Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full flex justify-center">
          {step === "choice" ? <Choice onPartner={() => setStep("partner")} /> : <PartnerForm onBack={() => setStep("choice")} />}
        </motion.div>
      </div>
    </div>
  );
}
